import { useState, useMemo, useCallback, useEffect } from "react";
import { AppLayout } from "@/components/layout";
import {
  useGetAdminCalendar,
  useGetAdminCalendarTrainerAvailability,
  useListTrainers,
  useListLocations,
  useCreateLocation,
  useUpdateLocation,
  useToggleLocationActive,
  getListLocationsQueryKey,
  CalendarEvent,
  type TrainerProfile,
  type TrainerAvailabilitySlot,
  type Location,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, X, Clock, Users, MapPin, Settings, UserCheck, Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { OperatingHoursPanel, useOperatingHoursForCalendar } from "@/components/admin/operating-hours-panel";
import { TrainerAvailabilityPanel } from "@/components/admin/trainer-availability-panel";
import { useAuth } from "@/hooks/use-auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CAL_HOUR_START = 6;
const CAL_HOUR_END = 22;
const CAL_TOTAL_HOURS = CAL_HOUR_END - CAL_HOUR_START;
const CAL_HOUR_HEIGHT = 64; // px per hour

type ViewMode = "week" | "day";

type SlotInfo = {
  day: Date;
  hour: number;
  dayOfWeek: number;
};

function getEventColorClasses(event: CalendarEvent): string {
  switch (event.status) {
    case "COMPLETED": return "bg-emerald-500/20 border-emerald-500/50 text-emerald-300";
    case "CONFIRMED": return "bg-red-500/20 border-red-500/50 text-red-300";
    case "CANCELLED": return "bg-gray-500/20 border-gray-500/50 text-gray-300";
    default: return "bg-red-500/20 border-red-500/50 text-red-300";
  }
}

function getDotColorClass(event: CalendarEvent): string {
  switch (event.status) {
    case "COMPLETED": return "bg-emerald-400";
    case "CONFIRMED": return "bg-red-400";
    case "CANCELLED": return "bg-gray-400";
    default: return "bg-red-400";
  }
}

function getEventsForSlot(events: CalendarEvent[], day: Date, hour: number): CalendarEvent[] {
  return events.filter(ev => {
    const start = parseISO(ev.start);
    return isSameDay(start, day) && start.getHours() === hour;
  });
}

function formatHour(hour: number): string {
  if (hour === 0 || hour === 24) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function isHourWithinOperatingHours(
  dayOfWeek: number,
  hour: number,
  _mode: string,
  blocks: { dayOfWeek: number; startTime: string; endTime: string }[]
): boolean {
  if (blocks.length === 0) return false;
  const slotStartMin = hour * 60;
  const slotEndMin = (hour + 1) * 60;
  return blocks.some(b => {
    if (b.dayOfWeek !== dayOfWeek) return false;
    const [sh, sm] = b.startTime.split(":").map(Number);
    const [eh, em] = b.endTime.split(":").map(Number);
    const blockStart = sh * 60 + sm;
    const blockEnd = eh * 60 + em;
    return slotStartMin < blockEnd && slotEndMin > blockStart;
  });
}

function getAvailableTrainersForSlot(
  dayOfWeek: number,
  hour: number,
  allAvailBlocks: TrainerAvailabilitySlot[]
): TrainerAvailabilitySlot[] {
  const slotStartMin = hour * 60;
  const slotEndMin = (hour + 1) * 60;
  const matching = allAvailBlocks.filter(b => {
    if (b.dayOfWeek !== dayOfWeek) return false;
    const [sh, sm] = b.startTime.split(":").map(Number);
    const [eh, em] = b.endTime.split(":").map(Number);
    const blockStart = sh * 60 + sm;
    const blockEnd = eh * 60 + em;
    return slotStartMin < blockEnd && slotEndMin > blockStart;
  });
  const seen = new Set<string>();
  return matching.filter(b => {
    if (seen.has(b.trainerId)) return false;
    seen.add(b.trainerId);
    return true;
  });
}

function getBookedTrainerIdsForSlot(
  events: CalendarEvent[],
  day: Date,
  hour: number
): Set<string> {
  const slotEvents = events.filter(ev => {
    if (ev.status === "CANCELLED") return false;
    const start = parseISO(ev.start);
    const end = parseISO(ev.end);
    const slotStart = new Date(day);
    slotStart.setHours(hour, 0, 0, 0);
    const slotEnd = new Date(day);
    slotEnd.setHours(hour + 1, 0, 0, 0);
    return start < slotEnd && end > slotStart;
  });
  const ids = new Set<string>();
  slotEvents.forEach(ev => {
    if (ev.trainerId) ids.add(ev.trainerId);
  });
  return ids;
}

function EventDetailModal({ event, onClose }: { event: CalendarEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <Card
        className="glass-panel border-white/10 w-full max-w-sm relative z-10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className={cn("w-2.5 h-2.5 rounded-full shrink-0 mt-0.5", getDotColorClass(event))} />
              <div>
                <CardTitle className="text-white text-base">{event.title}</CardTitle>
                <Badge variant="secondary" className={cn("mt-1 text-[10px]", getEventColorClasses(event))}>
                  Private Session · {event.status}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1 -mr-1" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-white font-semibold">
                {format(parseISO(event.start), "EEEE, MMM d")}
              </p>
              <p className="text-muted-foreground text-xs">
                {format(parseISO(event.start), "h:mm a")} — {format(parseISO(event.end), "h:mm a")}
              </p>
            </div>
          </div>
          {event.trainerName && (
            <div className="flex items-center gap-3 text-sm">
              <Users className="w-4 h-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-white font-semibold">{event.trainerName}</p>
                {event.studentName && (
                  <p className="text-muted-foreground text-xs">with {event.studentName}</p>
                )}
              </div>
            </div>
          )}
          {event.locationName && (
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
              <p className="text-white">{event.locationName}</p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground uppercase tracking-wider">
              {event.mode}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TrainerAvailabilityPopup({
  slotInfo,
  availableTrainers,
  bookedTrainerIds,
  onClose,
}: {
  slotInfo: SlotInfo;
  availableTrainers: TrainerAvailabilitySlot[];
  bookedTrainerIds: Set<string>;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <Card
        className="glass-panel border-white/10 w-full max-w-sm relative z-10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary shrink-0" />
              <div>
                <CardTitle className="text-white text-base">Trainer Availability</CardTitle>
                <p className="text-muted-foreground text-xs mt-0.5">
                  {format(slotInfo.day, "EEEE, MMM d")} · {formatHour(slotInfo.hour)} — {formatHour(slotInfo.hour + 1)}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1 -mr-1" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {availableTrainers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
              <Users className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No trainers available at this time.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableTrainers.map(trainer => {
                const isBooked = bookedTrainerIds.has(trainer.trainerId);
                return (
                  <div
                    key={trainer.trainerId}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg border",
                      isBooked
                        ? "border-red-500/30 bg-red-500/5"
                        : "border-white/10 bg-white/[0.03]"
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                      isBooked
                        ? "bg-red-500/20 text-red-300"
                        : "bg-yellow-500/20 text-yellow-300"
                    )}>
                      {trainer.trainerName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm font-semibold truncate",
                        isBooked ? "text-red-300" : "text-white"
                      )}>
                        {trainer.trainerName}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {isBooked ? "Booked" : "Available"}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] shrink-0",
                        isBooked
                          ? "border-red-500/30 text-red-400"
                          : "border-yellow-500/30 text-yellow-400"
                      )}
                    >
                      {isBooked ? "Booked" : "Free"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LocationManagementModal({
  open,
  onOpenChange,
  locations,
  gymId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locations: Location[];
  gymId: string;
}) {
  const queryClient = useQueryClient();
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");

  const createMutation = useCreateLocation({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(gymId) });
        resetForm();
      },
    },
  });

  const updateMutation = useUpdateLocation({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(gymId) });
        resetForm();
      },
    },
  });

  const toggleActiveMutation = useToggleLocationActive({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(gymId) });
      },
    },
  });

  function resetForm() {
    setIsAdding(false);
    setEditingLocation(null);
    setName("");
    setAddress("");
  }

  function startEdit(loc: Location) {
    setEditingLocation(loc);
    setIsAdding(false);
    setName(loc.name);
    setAddress(loc.address ?? "");
  }

  function startAdd() {
    setIsAdding(true);
    setEditingLocation(null);
    setName("");
    setAddress("");
  }

  function handleSave() {
    if (!name.trim()) return;
    if (editingLocation) {
      updateMutation.mutate({
        id: editingLocation.id,
        data: { name: name.trim(), address: address.trim() || undefined },
      });
    } else {
      createMutation.mutate({
        data: {
          gymId,
          name: name.trim(),
          address: address.trim() || "",
          city: "",
          state: "",
        },
      });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="glass-panel border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Manage Locations
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
          {locations.map(loc => (
            <div
              key={loc.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border",
                loc.isActive
                  ? "border-white/10 bg-white/[0.03]"
                  : "border-white/5 bg-white/[0.01] opacity-50"
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white truncate">{loc.name}</p>
                  {loc.isPrimary && (
                    <Badge variant="secondary" className="text-[10px] bg-primary/20 text-primary border-primary/30">
                      Primary
                    </Badge>
                  )}
                  {!loc.isActive && (
                    <Badge variant="secondary" className="text-[10px] bg-zinc-500/20 text-zinc-400">
                      Inactive
                    </Badge>
                  )}
                </div>
                {loc.address && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{loc.address}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => startEdit(loc)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                {!loc.isPrimary && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-red-400"
                    onClick={() => toggleActiveMutation.mutate({ id: loc.id, data: { isActive: !loc.isActive } })}
                    disabled={toggleActiveMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {(isAdding || editingLocation) && (
          <div className="space-y-3 pt-2 border-t border-white/10">
            <p className="text-sm font-semibold text-white">
              {editingLocation ? "Edit Location" : "Add New Location"}
            </p>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Downtown Gym"
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Address (optional)</Label>
              <Input
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="e.g. 123 Main St"
                className="h-9"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={!name.trim() || isSaving}>
                {isSaving ? "Saving..." : editingLocation ? "Update" : "Add"}
              </Button>
              <Button size="sm" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-row gap-2">
          {!isAdding && !editingLocation && (
            <Button size="sm" className="gap-1.5" onClick={startAdd}>
              <Plus className="w-3.5 h-3.5" />
              Add Location
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCalendar() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState<string>("all");
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");
  const [locationMgmtOpen, setLocationMgmtOpen] = useState(false);

  const gymId = user?.gymId ?? "";

  const { data: locations = [] } = useListLocations(gymId, {
    query: { enabled: !!gymId },
  });
  const activeLocations = locations.filter((l: Location) => l.isActive);

  useEffect(() => {
    if (selectedLocationId !== "all" && activeLocations.length > 0 && !activeLocations.some((l: Location) => l.id === selectedLocationId)) {
      setSelectedLocationId("all");
    }
  }, [selectedLocationId, activeLocations]);

  const calendarLocationId = selectedLocationId === "all" ? undefined : selectedLocationId;

  const { mode: opMode, blocks: opBlocks, locationName } = useOperatingHoursForCalendar(calendarLocationId);

  const { data: trainers = [] } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });
  const activeTrainers = (trainers as TrainerProfile[]).filter(t => t.id);

  const { data: allTrainerAvailability = [] } = useGetAdminCalendarTrainerAvailability(
    calendarLocationId ? { locationId: calendarLocationId } : undefined,
    { query: { retry: false } },
  );

  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 0 }),
    [currentDate]
  );

  const { data: allEvents, isLoading } = useGetAdminCalendar(
    calendarLocationId ? { locationId: calendarLocationId } : undefined,
    { query: { retry: false } },
  );

  const events: CalendarEvent[] = allEvents ?? [];

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const eventsForPeriod = useMemo(() => {
    if (viewMode === "day") {
      return events.filter(ev => isSameDay(parseISO(ev.start), currentDate));
    }
    return events.filter(ev => {
      const start = parseISO(ev.start);
      return start >= weekStart && start < addDays(weekStart, 7);
    });
  }, [events, weekStart, currentDate, viewMode]);

  const filteredEvents = useMemo(() => {
    if (selectedTrainerId === "all") return eventsForPeriod;
    return eventsForPeriod.filter(ev => ev.trainerId === selectedTrainerId);
  }, [eventsForPeriod, selectedTrainerId]);

  const totalSessions = eventsForPeriod.length;

  const navigatePrev = () => {
    if (viewMode === "day") {
      setCurrentDate(d => addDays(d, -1));
    } else {
      setCurrentDate(d => subWeeks(d, 1));
    }
  };

  const navigateNext = () => {
    if (viewMode === "day") {
      setCurrentDate(d => addDays(d, 1));
    } else {
      setCurrentDate(d => addWeeks(d, 1));
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  const headerLabel = viewMode === "day"
    ? format(currentDate, "EEEE, MMMM d, yyyy")
    : format(weekStart, "MMMM yyyy");

  const getSlotStatus = useCallback((dayOfWeek: number, hour: number, day: Date): "closed" | "open" | "yellow" => {
    if (!isHourWithinOperatingHours(dayOfWeek, hour, opMode, opBlocks)) {
      return "closed";
    }
    const availTrainers = getAvailableTrainersForSlot(dayOfWeek, hour, allTrainerAvailability);
    if (availTrainers.length === 0) return "open";
    const bookedIds = getBookedTrainerIdsForSlot(events, day, hour);
    const freeTrainers = availTrainers.filter(t => !bookedIds.has(t.trainerId));
    if (freeTrainers.length === 0) return "open";
    return "yellow";
  }, [opMode, opBlocks, allTrainerAvailability, events]);

  const handleSlotClick = useCallback((day: Date, hour: number, dayOfWeek: number) => {
    const status = getSlotStatus(dayOfWeek, hour, day);
    if (status === "closed") return;
    setSelectedSlot({ day, hour, dayOfWeek });
  }, [getSlotStatus]);

  const popupAvailableTrainers = useMemo(() => {
    if (!selectedSlot) return [];
    return getAvailableTrainersForSlot(selectedSlot.dayOfWeek, selectedSlot.hour, allTrainerAvailability);
  }, [selectedSlot, allTrainerAvailability]);

  const popupBookedTrainerIds = useMemo(() => {
    if (!selectedSlot) return new Set<string>();
    return getBookedTrainerIdsForSlot(events, selectedSlot.day, selectedSlot.hour);
  }, [selectedSlot, events]);

  function getSlotBgClass(status: "closed" | "open" | "yellow"): string {
    switch (status) {
      case "closed": return "bg-zinc-800/40";
      case "yellow": return "bg-yellow-400/10 outline outline-2 -outline-offset-2 outline-yellow-400";
      case "open": return "bg-white/[0.06]";
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Master Calendar</h1>
            <p className="text-muted-foreground text-lg">All private sessions across your gym.</p>
          </div>
          <div className="flex items-center gap-3">
            {activeLocations.length > 0 && (
              <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger className="h-9 w-[180px] text-sm">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {activeLocations.map((loc: Location) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setLocationMgmtOpen(true)}
            >
              <Building2 className="w-3.5 h-3.5" />
              Locations
            </Button>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <span className="w-2.5 h-2.5 rounded-full bg-primary" />
            <span className="text-sm font-semibold text-primary">{totalSessions} Private Sessions</span>
          </div>
          {opMode === "CUSTOM" && locationName && opBlocks.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20">
              <Clock className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400">Custom Hours</span>
            </div>
          )}
        </div>

        {opMode === "CUSTOM" && opBlocks.length === 0 && locationName && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Set your operating hours</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configure when your gym is open so students can only book during those times.
                </p>
              </div>
              <Button size="sm" className="shrink-0" onClick={() => setHoursOpen(true)}>
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Configure Hours
              </Button>
            </CardContent>
          </Card>
        )}


        <Card className="glass-panel border-white/5 overflow-hidden">
          <CardHeader className="pb-0 border-b border-white/5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">
                {headerLabel}
              </CardTitle>
              <div className="flex items-center gap-2">
                {activeTrainers.length > 0 && (
                  <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
                    <SelectTrigger className="h-8 w-[160px] text-xs">
                      <SelectValue placeholder="All Trainers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trainers</SelectItem>
                      {activeTrainers.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.firstName} {t.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5"
                  onClick={() => setAvailabilityOpen(true)}
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  Availability
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5"
                  onClick={() => setHoursOpen(true)}
                >
                  <Settings className="w-3.5 h-3.5" />
                  Hours
                </Button>
                <div className="flex items-center border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("day")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-semibold transition-colors",
                      viewMode === "day" ? "bg-primary text-black" : "text-muted-foreground hover:text-white"
                    )}
                  >
                    Day
                  </button>
                  <button
                    onClick={() => setViewMode("week")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-semibold transition-colors border-l border-white/10",
                      viewMode === "week" ? "bg-primary text-black" : "text-muted-foreground hover:text-white"
                    )}
                  >
                    Week
                  </button>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navigatePrev}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-xs" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navigateNext}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {viewMode === "week" && (
              <div className="grid grid-cols-[60px_repeat(7,1fr)] mt-4">
                <div />
                {weekDays.map((day, i) => (
                  <button
                    key={i}
                    onClick={() => { setCurrentDate(day); setViewMode("day"); }}
                    className="text-center pb-3 hover:bg-white/[0.03] rounded-lg transition-colors"
                  >
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">{DAY_LABELS[i]}</p>
                    <p className={cn(
                      "text-lg font-bold mt-0.5",
                      isSameDay(day, new Date()) ? "text-primary" : "text-white"
                    )}>{format(day, "d")}</p>
                  </button>
                ))}
              </div>
            )}

            {viewMode === "day" && (
              <div className="mt-3 pb-3">
                <p className={cn(
                  "text-base font-semibold",
                  isSameDay(currentDate, new Date()) ? "text-primary" : "text-white"
                )}>
                  {format(currentDate, "EEEE, MMMM d")}
                  {isSameDay(currentDate, new Date()) && (
                    <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Today</span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {filteredEvents.filter(ev => isSameDay(parseISO(ev.start), currentDate)).length} event{filteredEvents.filter(ev => isSameDay(parseISO(ev.start), currentDate)).length !== 1 ? "s" : ""} today
                </p>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : viewMode === "week" ? (
              <div className="overflow-auto max-h-[600px]">
                {/* Precise-position week grid */}
                <div
                  className="relative grid"
                  style={{ gridTemplateColumns: "60px repeat(7, 1fr)", height: `${CAL_TOTAL_HOURS * CAL_HOUR_HEIGHT}px` }}
                >
                  {/* Hour labels column */}
                  <div className="relative border-r border-white/[0.04]">
                    {Array.from({ length: CAL_TOTAL_HOURS }, (_, i) => CAL_HOUR_START + i).map(h => (
                      <div
                        key={h}
                        className="absolute w-full flex items-start justify-end pr-2"
                        style={{ top: `${(h - CAL_HOUR_START) * CAL_HOUR_HEIGHT}px`, height: `${CAL_HOUR_HEIGHT}px` }}
                      >
                        <span className="text-[10px] text-muted-foreground/60 font-mono pt-1 leading-none">
                          {formatHour(h)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {weekDays.map((day, di) => {
                    const dayEvents = filteredEvents.filter(ev => isSameDay(parseISO(ev.start), day));
                    const isNowDay = isSameDay(day, new Date());
                    const nowMins = (new Date().getHours() - CAL_HOUR_START) * 60 + new Date().getMinutes();
                    const nowTop = (nowMins / 60) * CAL_HOUR_HEIGHT;
                    const showNow = isNowDay && nowMins >= 0 && nowMins <= CAL_TOTAL_HOURS * 60;

                    return (
                      <div
                        key={di}
                        className={cn(
                          "relative border-r border-white/[0.04] last:border-r-0",
                          isNowDay && "bg-primary/[0.02]"
                        )}
                      >
                        {/* Hour slot backgrounds (for operating-hours coloring + click) */}
                        {Array.from({ length: CAL_TOTAL_HOURS }, (_, i) => CAL_HOUR_START + i).map(h => {
                          const status = getSlotStatus(day.getDay(), h, day);
                          return (
                            <div
                              key={h}
                              onClick={() => status !== "closed" && handleSlotClick(day, h, day.getDay())}
                              className={cn(
                                "absolute left-0 right-0 border-t border-white/[0.04]",
                                status === "closed" && "bg-zinc-800/40",
                                status === "yellow" && "bg-yellow-400/[0.07]",
                                status !== "closed" && "cursor-pointer hover:bg-white/[0.03]"
                              )}
                              style={{
                                top: `${(h - CAL_HOUR_START) * CAL_HOUR_HEIGHT}px`,
                                height: `${CAL_HOUR_HEIGHT}px`,
                              }}
                            />
                          );
                        })}

                        {/* Now line */}
                        {showNow && (
                          <div
                            className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
                            style={{ top: `${nowTop}px` }}
                          >
                            <div className="w-2 h-2 rounded-full bg-primary -ml-1 shrink-0" />
                            <div className="flex-1 border-t border-primary" />
                          </div>
                        )}

                        {/* Session blocks */}
                        {dayEvents.map(ev => {
                          const start = parseISO(ev.start);
                          const end = parseISO(ev.end);
                          const startMin = (start.getHours() - CAL_HOUR_START) * 60 + start.getMinutes();
                          const duration = Math.max((end.getTime() - start.getTime()) / 60000, 15);
                          const top = (startMin / 60) * CAL_HOUR_HEIGHT;
                          const height = Math.max((duration / 60) * CAL_HOUR_HEIGHT, 24);

                          return (
                            <button
                              key={ev.id}
                              onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                              className={cn(
                                "absolute left-0.5 right-0.5 rounded-md border px-1.5 py-1 text-left z-10 overflow-hidden hover:opacity-80 transition-opacity",
                                getEventColorClasses(ev)
                              )}
                              style={{ top: `${top}px`, height: `${height}px` }}
                              title={`${ev.title} · ${format(start, "h:mm a")}–${format(end, "h:mm a")}`}
                            >
                              <div className="flex items-center gap-1 truncate">
                                <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", getDotColorClass(ev))} />
                                <span className="text-[11px] font-semibold truncate leading-tight">{ev.title}</span>
                              </div>
                              {height > 38 && (
                                <p className="text-[10px] opacity-70 leading-tight mt-0.5 truncate">
                                  {format(start, "h:mm")}–{format(end, "h:mm a")}
                                </p>
                              )}
                              {height > 56 && ev.trainerName && (
                                <p className="text-[10px] opacity-60 leading-tight truncate">{ev.trainerName}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                {filteredEvents.length === 0 && allTrainerAvailability.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-48 text-center gap-3">
                    <Calendar className="w-10 h-10 text-muted-foreground" />
                    <p className="text-muted-foreground">No events scheduled this week.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-auto max-h-[600px]">
                {HOURS.map(hour => {
                  const slotEvents = getEventsForSlot(filteredEvents, currentDate, hour);
                  const status = getSlotStatus(currentDate.getDay(), hour, currentDate);
                  return (
                    <div key={hour} className="grid grid-cols-[60px_1fr] border-b border-white/[0.04] min-h-[80px]">
                      <div className="flex items-start justify-end pr-3 pt-2">
                        <span className="text-[11px] text-muted-foreground font-mono">{formatHour(hour)}</span>
                      </div>
                      <div
                        onClick={() => status !== "closed" && handleSlotClick(currentDate, hour, currentDate.getDay())}
                        className={cn(
                          "p-2 flex flex-col gap-2 relative",
                          status === "yellow" && "z-10",
                          getSlotBgClass(status),
                          status !== "closed" && "cursor-pointer hover:bg-white/[0.05]"
                        )}
                      >
                        {status === "yellow" && slotEvents.length === 0 && (
                          <div className="flex items-center gap-2 py-2">
                            <span className="text-[10px] text-primary/80 uppercase tracking-wider font-semibold">Trainer Available</span>
                          </div>
                        )}
                        {status === "closed" && slotEvents.length === 0 && (
                          <div className="flex items-center gap-2 py-2">
                            <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">Closed</span>
                          </div>
                        )}
                        {status === "open" && slotEvents.length === 0 && (
                          <div className="flex items-center gap-2 py-2">
                            <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Open</span>
                          </div>
                        )}
                        {slotEvents.map(ev => (
                          <button
                            key={ev.id}
                            onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                            className={cn(
                              "w-full text-left px-3 py-2.5 rounded-xl border text-sm font-semibold transition-opacity hover:opacity-80",
                              getEventColorClasses(ev)
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span className={cn("w-2 h-2 rounded-full shrink-0", getDotColorClass(ev))} />
                              <span>{ev.title}</span>
                              <span className="ml-auto text-xs font-normal opacity-75">
                                {format(parseISO(ev.start), "h:mm a")} — {format(parseISO(ev.end), "h:mm a")}
                              </span>
                            </div>
                            {(ev.trainerName || ev.studentName) && (
                              <p className="text-xs font-normal opacity-75 mt-1 ml-4">
                                {ev.trainerName}{ev.studentName ? ` · with ${ev.studentName}` : ""}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {filteredEvents.filter(ev => isSameDay(parseISO(ev.start), currentDate)).length === 0 && allTrainerAvailability.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-48 text-center gap-3">
                    <Calendar className="w-10 h-10 text-muted-foreground" />
                    <p className="text-muted-foreground">No events scheduled for this day.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-white/[0.08] border border-white/20" />
            <span>Space Open</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-yellow-400/10 outline outline-2 -outline-offset-2 outline-yellow-400" />
            <span>Trainer Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-red-500/[0.15] border border-red-500/30" />
            <span>Booked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-zinc-800/50 border border-zinc-600/30" />
            <span>Closed</span>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}

      {selectedSlot && (
        <TrainerAvailabilityPopup
          slotInfo={selectedSlot}
          availableTrainers={popupAvailableTrainers}
          bookedTrainerIds={popupBookedTrainerIds}
          onClose={() => setSelectedSlot(null)}
        />
      )}

      <OperatingHoursPanel open={hoursOpen} onOpenChange={setHoursOpen} />
      <TrainerAvailabilityPanel open={availabilityOpen} onOpenChange={setAvailabilityOpen} />
      <LocationManagementModal
        open={locationMgmtOpen}
        onOpenChange={setLocationMgmtOpen}
        locations={locations as Location[]}
        gymId={gymId}
      />
    </AppLayout>
  );
}
